import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { getTopDriversWilson } from '../DELIVERY/services/reviewServices';
import {
  fetchCompletedServicesToday, fetchEarningsSince, fetchSearchingServicesCount,
  fetchPendingDocsCount, fetchOpenTicketsCount, getDriverRoleId, countUsersByRole,
  fetchAllWalletsSummary
} from '../../services/api/admin-ops';
import AdminKpiHeader from './dashboard/AdminKpiHeader';
import AdminWeeklyChart from './dashboard/AdminWeeklyChart';
import AdminTopDriversList from './dashboard/AdminTopDriversList';

export default function AdminDashboard({ navigate }) {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({
    tripsToday: 0,
    earningsToday: 0,
    commissionToday: 0,
    pendingServices: 0,
    activeDrivers: 0,
    pendingDocs: 0,
    openTickets: 0
  });
  const [chartData, setChartData] = useState([]);
  const [topDrivers, setTopDrivers] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  async function loadDashboardData() {
    setLoading(true);
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayIso = today.toISOString();

      const [
        { count: tripsCount },
        { data: earningsData },
        { count: pendingSrvCount },
        { count: pendingDocsCount },
        { count: openTicketsCount },
        topDriversData,
        { data: walletsData }
      ] = await Promise.all([
        fetchCompletedServicesToday(todayIso).catch(() => ({ count: 0, data: [] })),
        fetchEarningsSince(todayIso).catch(() => ({ count: 0, data: [] })),
        fetchSearchingServicesCount().catch(() => ({ count: 0, data: [] })),
        fetchPendingDocsCount().catch(() => ({ count: 0, data: [] })),
        fetchOpenTicketsCount().catch(() => ({ count: 0, data: [] })),
        getTopDriversWilson(5).catch(() => []),
        fetchAllWalletsSummary().catch(() => ({ data: [] }))
      ]);

      const earningsToday = (earningsData || []).reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
      const commissionToday = (earningsData || []).reduce((sum, item) => sum + (Number(item.commission_amount || item.commission) || 0), 0);
      const walletTotalBalance = (walletsData || []).reduce((sum, w) => sum + (Number(w.balance) || 0), 0);
      const walletsWithBalanceCount = (walletsData || []).filter(w => Number(w.balance || 0) > 0).length;

      const { data: roleData } = await getDriverRoleId();
      const { count: activeDriversCount } = await countUsersByRole(roleData?.id || '');

      setKpis({
        tripsToday: tripsCount || 0,
        earningsToday,
        commissionToday,
        walletTotalBalance,
        walletsWithBalanceCount,
        pendingServices: pendingSrvCount || 0,
        activeDrivers: activeDriversCount || 0,
        pendingDocs: pendingDocsCount || 0,
        openTickets: openTicketsCount || 0,
      });

      setTopDrivers(topDriversData || []);

      const last7Days = new Date();
      last7Days.setDate(last7Days.getDate() - 7);
      const { data: weekEarnings } = await fetchEarningsSince(last7Days.toISOString()).catch(() => ({ data: [] }));

      const chartMap = {};
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const dayStr = d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' });
        chartMap[dayStr] = { amount: 0, commission: 0 };
      }

      if (weekEarnings) {
        weekEarnings.forEach(e => {
          const dayStr = new Date(e.created_at).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' });
          if (chartMap[dayStr] !== undefined) {
            chartMap[dayStr].amount += Number(e.amount || 0);
            chartMap[dayStr].commission += Number(e.commission_amount || e.commission || 0);
          }
        });
      }

      const finalChartData = Object.keys(chartMap).map(k => ({ name: k, Ingresos: chartMap[k].amount, Comisiones: chartMap[k].commission }));
      setChartData(finalChartData);

    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  }

  const exportToCSV = () => {
    const headers = ['Fecha', 'Ingresos', 'Comisiones'];
    const rows = chartData.map(d => [d.name, d.Ingresos.toFixed(2), d.Comisiones.toFixed(2)]);
    const csvContent = 'data:text/csv;charset=utf-8,' 
      + headers.join(',') + '\n' 
      + rows.map(e => e.join(',')).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `dashboard_report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" style={{ color: 'var(--bry-accent, #0EABB0)' }} />
      </div>
    );
  }

  return (
    <div className="p-3 container-fluid" style={{ maxWidth: '1200px' }}>
      <AdminKpiHeader kpis={kpis} onNavigate={navigate} />
      <AdminWeeklyChart chartData={chartData} onExportCSV={exportToCSV} />
      <AdminTopDriversList topDrivers={topDrivers} onNavigate={navigate} />
    </div>
  );
}
